import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";

import { Alert } from "../base/Alert";
import { Button } from "../base/Button";
import LoadingWheel from "../shared/LoadingWheel";
import useApiHelper from "../../hooks/useApiHelper";
import { PinnacleMappingQueryKeys } from "../../data/PinnacleMapping/keys";
import { usePinnacleAffectedParts } from "../../data/PinnacleMapping/usePinnacleMapping";
import { IDefaultResponse } from "../../models/DefaultResponse";
import { IPinnacleAffectedPart, IPinnacleApplyResult, PinnaclePartStatus } from "../../models/PinnacleMapping/PinnacleMapping";
import { CategoryOption, CategoryPicker } from "./CategoryPicker";
import { partOutcome, partStatusMeta, tickable } from "./pinnacleMeta";
import { useNewCategory } from "./useNewCategory";

/**
 * Parts sent to Core per request. Listed parts each withdraw and rebuild an eBay offer, which takes
 * seconds, so small batches keep every request well inside its timeout and let the progress bar move.
 */
export const APPLY_BATCH_SIZE = 25;

interface ApplyRun {
  total: number;
  sent: number;
  moved: number;
  relisted: number;
  failed: number;
  errors: string[];
  stopped: boolean;
  finished: boolean;
}

/**
 * The customer's parts of one item type and where the current mappings would put each. Nothing moves
 * until someone ticks parts and applies: "Move" rows start ticked, everything else starts unticked,
 * and any single part can be sent somewhere else by hand.
 *
 * Ticked parts are moved in batches with a progress bar, and can be stopped between batches. Parts
 * already in the right category are hidden by default, so what is left is what still needs doing.
 */
export function AffectedPartsPanel({
  customerId,
  customerName,
  itemTypeId,
  categories,
}: {
  customerId: string;
  customerName?: string;
  itemTypeId: string;
  categories: CategoryOption[];
}) {
  const parts = usePinnacleAffectedParts(customerId, itemTypeId);
  const { post } = useApiHelper();
  const queryClient = useQueryClient();
  const newCategory = useNewCategory();

  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [ticks, setTicks] = useState<Record<string, boolean>>({});
  const [editing, setEditing] = useState<string | null>(null);
  const [hideDone, setHideDone] = useState(true);
  const [sentIds, setSentIds] = useState<Set<string>>(new Set());
  const [run, setRun] = useState<ApplyRun | null>(null);
  const stopRef = useRef(false);
  const headerTick = useRef<HTMLInputElement>(null);

  const running = !!run && !run.finished;

  // A fresh list from Core is the truth again: per-row choices and "sent" marks start over.
  useEffect(() => {
    setOverrides({});
    setTicks({});
    setEditing(null);
    setSentIds(new Set());
  }, [customerId, itemTypeId, parts.dataUpdatedAt]);

  useEffect(() => setRun(null), [customerId, itemTypeId]);

  const options = newCategory.withCreated(categories);
  const categoryName = useMemo(() => new Map(options.map((c) => [c.id, c.name])), [options]);

  const rows = useMemo(
    () =>
      (parts.data?.parts ?? []).map((part) => {
        const outcome = partOutcome(part, overrides[part.id]);
        const sent = sentIds.has(part.id);
        const canTick = !sent && tickable.includes(outcome.status);
        const ticked = canTick && (ticks[part.id] ?? outcome.status === "Move");
        return { part, ...outcome, sent, canTick, ticked, manual: overrides[part.id] !== undefined };
      }),
    [parts.data, overrides, ticks, sentIds],
  );

  const visible = hideDone ? rows.filter((r) => !r.sent && r.status !== "Unchanged") : rows;
  const hiddenCount = rows.length - visible.length;

  const tickableRows = visible.filter((r) => r.canTick);
  const tickedCount = tickableRows.filter((r) => r.ticked).length;
  const allTicked = tickableRows.length > 0 && tickedCount === tickableRows.length;

  useEffect(() => {
    if (headerTick.current) headerTick.current.indeterminate = tickedCount > 0 && !allTicked;
  }, [tickedCount, allTicked]);

  const chosen = rows.filter((r) => r.ticked && r.target);
  const chosenListed = chosen.filter((r) => r.part.listed).length;

  // Core counts every part of the item type; rows on screen then reflect any category picked by hand.
  const counts = useMemo(() => {
    const result: Partial<Record<PinnaclePartStatus, number>> = {};
    const server = parts.data?.counts ?? {};

    for (const status of Object.keys(partStatusMeta) as PinnaclePartStatus[]) {
      const n = server[status] ?? server[status.charAt(0).toLowerCase() + status.slice(1)];
      if (n) result[status] = n;
    }

    for (const r of rows) {
      if (r.status === r.part.status) continue;
      result[r.part.status] = Math.max(0, (result[r.part.status] ?? 1) - 1);
      result[r.status] = (result[r.status] ?? 0) + 1;
    }

    return result;
  }, [parts.data, rows]);

  const tickAll = (value: boolean) =>
    setTicks((t) => {
      const next = { ...t };
      for (const r of tickableRows) next[r.part.id] = value;
      return next;
    });

  const pick = (part: IPinnacleAffectedPart, categoryId: string | null) => {
    setEditing(null);
    setOverrides((o) => {
      const next = { ...o };
      if (!categoryId || categoryId === part.proposedCategoryId) delete next[part.id];
      else next[part.id] = categoryId;
      return next;
    });
    if (categoryId) setTicks((t) => ({ ...t, [part.id]: true }));
  };

  const apply = async () => {
    const queue = chosen.map((r) => ({ partId: r.part.id, categoryId: r.target! }));
    if (queue.length === 0) return;

    stopRef.current = false;
    setEditing(null);

    const totals: ApplyRun = { total: queue.length, sent: 0, moved: 0, relisted: 0, failed: 0, errors: [], stopped: false, finished: false };
    setRun({ ...totals });

    for (let i = 0; i < queue.length; i += APPLY_BATCH_SIZE) {
      if (stopRef.current) {
        totals.stopped = true;
        break;
      }

      const batch = queue.slice(i, i + APPLY_BATCH_SIZE);

      try {
        const res = await post<IDefaultResponse<IPinnacleApplyResult>>(`/pinnacle-mapping/customer/${customerId}/apply`, { parts: batch });

        if (res.data) {
          totals.moved += res.data.moved;
          totals.relisted += res.data.relisted;
          totals.failed += res.data.failed;
          totals.errors.push(...res.data.errors);
        } else {
          totals.failed += batch.length;
          totals.errors.push(res.message || "Core did not answer this batch.");
        }
      } catch (e) {
        totals.failed += batch.length;
        totals.errors.push(e instanceof Error ? e.message : "This batch could not be sent.");
      }

      totals.sent += batch.length;
      setSentIds((s) => new Set([...Array.from(s), ...batch.map((b) => b.partId)]));
      setRun({ ...totals, errors: [...totals.errors] });
    }

    setRun({ ...totals, errors: [...totals.errors], finished: true });

    // Reload from Core so the table shows where the parts really are now.
    queryClient.invalidateQueries({ queryKey: PinnacleMappingQueryKeys.all() });
  };

  return (
    <section className="space-y-3" aria-label="Affected parts">
      {newCategory.modal}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-base font-medium text-gray-900">Affected parts</h3>
          <p className="text-sm text-gray-500">
            {customerName ? `${customerName}'s` : "This customer's"} imported parts of this item type, and where the mappings above would
            put them. Nothing moves until you apply.
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(Object.keys(partStatusMeta) as PinnaclePartStatus[])
            .filter((s) => counts[s])
            .map((s) => (
              <span key={s} className={clsx("rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset", partStatusMeta[s].className)}>
                {counts[s]} {partStatusMeta[s].label}
              </span>
            ))}
        </div>
      </div>

      {run && <ApplyProgress run={run} onStop={() => (stopRef.current = true)} onDismiss={() => setRun(null)} />}

      {parts.isLoading && <LoadingWheel />}
      {parts.isError && <Alert severity="warn" title="Could not load this customer's parts" message={parts.error.message} />}

      {parts.data && parts.data.total === 0 && (
        <p className="text-sm text-gray-500">This customer has no imported parts of this item type yet.</p>
      )}

      {parts.data && parts.data.total > 0 && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
            <label htmlFor="pinnacle-hide-done" className="inline-flex items-center gap-2 text-gray-700">
              <input
                id="pinnacle-hide-done"
                type="checkbox"
                checked={hideDone}
                onChange={(e) => setHideDone(e.target.checked)}
                className="h-4 w-4 accent-orange-600"
              />
              Hide parts already in the right category
              {hideDone && hiddenCount > 0 && <span className="text-gray-400">({hiddenCount} hidden)</span>}
            </label>
            {parts.data.truncated && (
              <span className="text-gray-500">
                Showing {parts.data.parts.length} of {parts.data.total}, those needing action first. Move these and the rest load next.
              </span>
            )}
          </div>

          {visible.length === 0 ? (
            <p className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
              Every part shown is already in the right category.
            </p>
          ) : (
            <div className="max-h-[60vh] overflow-auto rounded-md border border-gray-200">
              <table className="min-w-[56rem] w-full text-sm">
                <thead className="sticky top-0 z-10 bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="w-8 px-3 py-2">
                      <input
                        ref={headerTick}
                        id="tick-all"
                        type="checkbox"
                        aria-label={allTicked ? "Untick all parts" : "Tick all parts"}
                        title={allTicked ? "Untick all" : "Tick all"}
                        checked={allTicked}
                        disabled={tickableRows.length === 0 || running}
                        onChange={() => tickAll(!allTicked)}
                        className="h-4 w-4 accent-orange-600"
                      />
                    </th>
                    <th className="px-3 py-2">Stock no</th>
                    <th className="px-3 py-2">Description</th>
                    <th className="px-3 py-2">Detected</th>
                    <th className="px-3 py-2">Current category</th>
                    <th className="px-3 py-2">Move to</th>
                    <th className="px-3 py-2">Outcome</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {visible.map(({ part, target, status, sent, canTick, ticked, manual }) => (
                    <tr key={part.id} className={clsx(ticked && "bg-orange-50/40", sent && "opacity-50")}>
                      <td className="px-3 py-2">
                        <input
                          id={`tick-${part.id}`}
                          type="checkbox"
                          aria-label={`Move part ${part.stockNumber ?? part.id}`}
                          checked={ticked}
                          disabled={!canTick || running}
                          onChange={(e) => setTicks((t) => ({ ...t, [part.id]: e.target.checked }))}
                          className="h-4 w-4 accent-orange-600"
                        />
                      </td>
                      <td className="px-3 py-2 font-mono tabular-nums">{part.stockNumber ?? "-"}</td>
                      <td className="max-w-xs px-3 py-2">{part.description || <span className="text-gray-400">(blank)</span>}</td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        {[part.detectedSide, part.detectedPosition].filter(Boolean).join(" · ") || "none"}
                      </td>
                      <td className="px-3 py-2">
                        {part.currentCategoryName ?? <span className="text-gray-400">Uncategorised</span>}
                        {part.listed && <span className="ml-1 rounded bg-amber-100 px-1 text-xs text-amber-800">eBay</span>}
                        {part.sold && <span className="ml-1 rounded bg-gray-100 px-1 text-xs text-gray-600">Sold</span>}
                      </td>
                      <td className="px-3 py-2">
                        {editing === part.id ? (
                          <CategoryPicker
                            categories={options}
                            value={target}
                            onChange={(id) => pick(part, id)}
                            onCreate={(typed) => newCategory.start(typed, (created) => pick(part, created.id))}
                            ariaLabel={`Category for part ${part.stockNumber}`}
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => setEditing(part.id)}
                            disabled={sent || running}
                            className="text-left hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-600 disabled:no-underline"
                          >
                            {target && target !== part.currentCategoryId ? (
                              <>
                                {categoryName.get(target) ?? part.proposedCategoryName ?? "Not on this customer yet"}
                                {manual && <span className="ml-1 rounded bg-blue-50 px-1 text-xs font-medium text-blue-700">Manual</span>}
                              </>
                            ) : (
                              <span className="text-gray-400">Choose…</span>
                            )}
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {sent ? (
                          <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                            Sent
                          </span>
                        ) : (
                          <span className={clsx("rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset", partStatusMeta[status].className)}>
                            {partStatusMeta[status].label}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-end gap-3">
            {chosenListed > 0 && !running && (
              <span className="text-sm text-amber-800">
                {chosenListed} ticked part{chosenListed === 1 ? " is" : "s are"} listed on eBay. Moving {chosenListed === 1 ? "it" : "them"}{" "}
                withdraws the listing and builds a new offer.
              </span>
            )}
            <Button onClick={apply} disabled={chosen.length === 0 || running}>
              {running ? "Moving…" : `Move ${chosen.length} ticked part${chosen.length === 1 ? "" : "s"}`}
            </Button>
          </div>
        </>
      )}
    </section>
  );
}

function ApplyProgress({ run, onStop, onDismiss }: { run: ApplyRun; onStop: () => void; onDismiss: () => void }) {
  const [showAll, setShowAll] = useState(false);
  const pct = run.total ? Math.round((run.sent / run.total) * 100) : 0;
  const errors = showAll ? run.errors : run.errors.slice(0, 5);

  const title = !run.finished
    ? `Moving parts: ${run.sent} of ${run.total} sent`
    : run.stopped
      ? `Stopped after ${run.sent} of ${run.total}`
      : `Finished: ${run.total} part${run.total === 1 ? "" : "s"} sent`;

  return (
    <div
      role="status"
      aria-live="polite"
      className={clsx(
        "space-y-2 rounded-md border p-3 text-sm",
        run.finished && run.failed === 0 ? "border-green-200 bg-green-50" : run.finished ? "border-amber-200 bg-amber-50" : "border-blue-200 bg-blue-50",
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium text-gray-900">{title}</span>
        {!run.finished ? (
          <Button variant="outlined" size="sm" onClick={onStop}>
            Stop after this batch
          </Button>
        ) : (
          <button type="button" onClick={onDismiss} className="text-xs font-medium text-gray-600 hover:underline">
            Dismiss
          </button>
        )}
      </div>

      <div className="h-2 overflow-hidden rounded-full bg-white ring-1 ring-inset ring-gray-200">
        <div
          className={clsx("h-full rounded-full transition-[width] duration-300", run.failed ? "bg-amber-500" : "bg-orange-600")}
          style={{ width: `${pct}%` }}
        />
      </div>

      <p className="tabular-nums text-gray-700">
        {run.moved} moved
        {run.relisted > 0 && ` · ${run.relisted} relisted on eBay`}
        {run.failed > 0 && ` · ${run.failed} could not be moved`}
        {!run.finished && ` · ${pct}%`}
      </p>

      {run.errors.length > 0 && (
        <div className="space-y-1">
          <ul className="list-inside list-disc text-xs text-amber-900">
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
          {run.errors.length > 5 && (
            <button type="button" onClick={() => setShowAll(!showAll)} className="text-xs font-medium text-gray-600 hover:underline">
              {showAll ? "Show fewer" : `Show all ${run.errors.length} problems`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
